import { questionOptions } from "../../../services/questionOptions";

export const TYPE_LABELS = { text: "Texto abierto", number: "Numérica", single: "Opción única", multi: "Opción múltiple" };
const API_TYPES = { TEXT: "text", OPEN_TEXT: "text", NUMERIC: "number", SINGLE_OPTION: "single", MULTIPLE_OPTION: "multi", MULTIPLE_CHOICE: "multi" };

export function buildCategoryIndex(raw = []) {
  const byId = new Map();
  raw.forEach((category) => {
    if (!category.parent) byId.set(category.id, { ...category, parentId: null });
    else {
      byId.set(category.id, { ...category, parentId: category.parent.id });
      if (!byId.has(category.parent.id)) byId.set(category.parent.id, { ...category.parent, parentId: null });
    }
    (category.subcategories || []).forEach((sub) => byId.set(sub.id, { ...sub, parentId: category.id }));
  });
  return byId;
}

export function normalizeQuestion(question, categories) {
  const category = categories.get(question.category?.id || question.categoryId);
  const parentId = category?.parentId || question.category?.parent?.id;
  const type = API_TYPES[question.answerType] || null;
  return {
    id: question.id,
    text: question.text || "",
    type,
    categoryId: parentId || category?.id || question.category?.id || question.categoryId || null,
    categoryName: parentId ? categories.get(parentId)?.name || question.category?.parent?.name || "" : category?.name || question.category?.name || "",
    subcategoryId: parentId ? category?.id || question.category?.id : null,
    subcategoryName: parentId ? category?.name || question.category?.name || "" : "",
    required: !!question.required,
    options: questionOptions(question.answers),
  };
}

export function toEditorContent(questionnaire) {
  const seen = new Set();
  return {
    name: questionnaire.name || "",
    questions: [...(questionnaire.questions || [])]
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .filter((question) => {
        if (!question.id || seen.has(question.id)) return false;
        seen.add(question.id);
        return true;
      })
      .map(({ id, required }) => ({ id, required: !!required })),
  };
}

export function contentSignature(content) {
  return JSON.stringify({ name: content.name.trim(), questions: content.questions });
}

export function appendQuestions(content, questions) {
  const ids = new Set(content.questions.map((question) => question.id));
  const additions = questions.filter((question) => {
    if (ids.has(question.id)) return false;
    ids.add(question.id);
    return true;
  }).map(({ id, required }) => ({ id, required: !!required }));
  return { ...content, questions: [...content.questions, ...additions] };
}

export function moveQuestion(content, questionId, direction) {
  const index = content.questions.findIndex((question) => question.id === questionId);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= content.questions.length) return content;
  const questions = [...content.questions];
  [questions[index], questions[target]] = [questions[target], questions[index]];
  return { ...content, questions };
}

export function buildEditorPayload(content, questionnaire) {
  // The PUT endpoint also replaces assignment settings, so preserve them explicitly.
  return {
    name: content.name.trim(),
    questions: content.questions.map((question, position) => ({ ...question, position })),
    defaultAssignment: questionnaire.defaultAssignment ?? true,
    admin: questionnaire.defaultAssignment !== false || !questionnaire.admin ? null : { id: questionnaire.admin.id },
  };
}

export function parseDraft(value) {
  try {
    const draft = JSON.parse(value);
    if (draft?.version !== 1 || typeof draft.content?.name !== "string" || !Array.isArray(draft.content.questions)) return null;
    const ids = new Set();
    for (const question of draft.content.questions) {
      if (typeof question.id !== "string" || !question.id || typeof question.required !== "boolean" || ids.has(question.id)) return null;
      ids.add(question.id);
    }
    return { name: draft.content.name, questions: draft.content.questions.map(({ id, required }) => ({ id, required })) };
  } catch {
    return null;
  }
}
